import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import {
  Upload, FileText, HardDrive, Download, Lock, Calendar, RefreshCw,
  ExternalLink, AlertTriangle, TrendingUp, Clock, Shield, Wrench,
  Repeat, Zap, BarChart3, QrCode, KeyRound, Link2, ImageIcon, Palette,
  MousePointerClick, Hash,
} from 'lucide-react';

/* ── Types ── */
type Tab = 'upload' | 'converter' | 'qrcode' | 'passwort' | 'kurzlinks' | 'bildbearbeitung' | 'farbpalette';

interface UploadStats {
  totalShares: number;
  activeShares: number;
  totalFiles: number;
  totalSizeBytes: number;
  totalDownloads: number;
  passwordProtected: number;
  sharesToday: number;
  sharesThisWeek: number;
  sharesThisMonth: number;
}

interface Share {
  id: string;
  slug: string;
  title: string | null;
  password_protected: boolean;
  expires_at: string;
  max_downloads: number | null;
  current_downloads: number;
  created_at: string;
  deleted_at: string | null;
  file_count: number;
  total_size: number;
}

interface ConverterStats {
  totalConversions: number;
  totalInputBytes: number;
  totalOutputBytes: number;
  conversionsToday: number;
  byType: Record<string, number>;
  errors: number;
  startedAt: string;
  tmpFileCount: number;
  tmpSizeBytes: number;
  uptime: number;
}

interface ToolsStats {
  qrcode: number;
  image: number;
  colors: number;
  shortlinks: {
    total: number;
    totalClicks: number;
    today: number;
    topLinks: Array<{ code: string; target_url: string; clicks: number; created_at: string }>;
  };
  startedAt: string;
  uptime: number;
}

/* ── Helpers ── */
function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function isExpired(iso: string): boolean {
  return new Date(iso) < new Date();
}

/* ── Styles ── */
const cardCls = 'bg-white border border-slate-200 rounded-xl p-5 shadow-sm';
const statCls = 'bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex items-center gap-4';
const iconBgCls = (color: string) => `w-12 h-12 rounded-lg flex items-center justify-center ${color}`;
const btnPrimary = 'inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition';
const btnSecondary = 'inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-md hover:bg-slate-200 transition';
const badgeCls = (color: string) => `inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${color}`;

const TABS: Array<{ id: Tab; label: string; icon: typeof Upload }> = [
  { id: 'upload', label: 'Upload', icon: Upload },
  { id: 'converter', label: 'Converter', icon: Repeat },
  { id: 'qrcode', label: 'QR-Code', icon: QrCode },
  { id: 'passwort', label: 'Passwort', icon: KeyRound },
  { id: 'kurzlinks', label: 'Kurzlinks', icon: Link2 },
  { id: 'bildbearbeitung', label: 'Bilder', icon: ImageIcon },
  { id: 'farbpalette', label: 'Farben', icon: Palette },
];

export function ToolsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('upload');
  const [uploadStats, setUploadStats] = useState<UploadStats | null>(null);
  const [shares, setShares] = useState<Share[]>([]);
  const [uploadConnected, setUploadConnected] = useState(true);
  const [converterStats, setConverterStats] = useState<ConverterStats | null>(null);
  const [converterConnected, setConverterConnected] = useState(true);
  const [toolsStats, setToolsStats] = useState<ToolsStats | null>(null);
  const [toolsConnected, setToolsConnected] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRaw, sharesRaw, cvtRaw, toolsRaw] = await Promise.all([
        api.get('/upload/stats'),
        api.get('/upload/shares?limit=100'),
        api.get('/upload/converter/stats'),
        api.get('/upload/tools/stats'),
      ]);
      const statsRes = statsRaw as { connected: boolean; stats: UploadStats | null };
      const sharesRes = sharesRaw as { shares: Share[] };
      const cvtRes = cvtRaw as { connected: boolean; stats: ConverterStats | null };
      const toolsRes = toolsRaw as { connected: boolean; stats: ToolsStats | null };

      setUploadConnected(statsRes.connected);
      setUploadStats(statsRes.stats);
      setShares(sharesRes.shares ?? []);
      setConverterConnected(cvtRes.connected);
      setConverterStats(cvtRes.stats);
      setToolsConnected(toolsRes.connected);
      setToolsStats(toolsRes.stats);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <RefreshCw className="animate-spin mr-3" size={20} />
        Lade Statistiken...
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${cardCls} text-center py-12`}>
        <AlertTriangle className="mx-auto mb-3 text-red-500" size={32} />
        <p className="text-red-600 font-medium">{error}</p>
        <button onClick={load} className={`${btnPrimary} mt-4`}>
          <RefreshCw size={16} /> Erneut versuchen
        </button>
      </div>
    );
  }

  /* Badge-Zähler für Tabs */
  const tabBadge = (id: Tab): React.ReactNode => {
    if (id === 'upload' && uploadConnected && uploadStats)
      return <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full">{uploadStats.activeShares}</span>;
    if (id === 'converter' && converterConnected && converterStats)
      return <span className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded-full">{converterStats.totalConversions}</span>;
    if (id === 'qrcode' && toolsConnected && toolsStats)
      return <span className="bg-emerald-100 text-emerald-700 text-xs px-1.5 py-0.5 rounded-full">{toolsStats.qrcode}</span>;
    if (id === 'kurzlinks' && toolsConnected && toolsStats)
      return <span className="bg-sky-100 text-sky-700 text-xs px-1.5 py-0.5 rounded-full">{toolsStats.shortlinks.total}</span>;
    if (id === 'bildbearbeitung' && toolsConnected && toolsStats)
      return <span className="bg-orange-100 text-orange-700 text-xs px-1.5 py-0.5 rounded-full">{toolsStats.image}</span>;
    if (id === 'farbpalette' && toolsConnected && toolsStats)
      return <span className="bg-pink-100 text-pink-700 text-xs px-1.5 py-0.5 rounded-full">{toolsStats.colors}</span>;
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Wrench className="text-blue-600" size={28} />
            Tools
          </h1>
          <p className="text-slate-500 text-sm mt-1">Alle Tools & Statistiken</p>
        </div>
        <button onClick={load} className={btnPrimary}>
          <RefreshCw size={16} /> Aktualisieren
        </button>
      </div>

      {/* Tab-Navigation */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition whitespace-nowrap ${
              activeTab === id
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Icon size={16} />
            {label}
            {tabBadge(id)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'upload' && (
        <UploadTab stats={uploadStats} shares={shares} connected={uploadConnected} />
      )}
      {activeTab === 'converter' && (
        <ConverterTab stats={converterStats} connected={converterConnected} />
      )}
      {activeTab === 'qrcode' && (
        <QrCodeTab stats={toolsStats} connected={toolsConnected} />
      )}
      {activeTab === 'passwort' && (
        <PasswortTab stats={toolsStats} connected={toolsConnected} />
      )}
      {activeTab === 'kurzlinks' && (
        <KurzlinksTab stats={toolsStats} connected={toolsConnected} />
      )}
      {activeTab === 'bildbearbeitung' && (
        <BildbearbeitungTab stats={toolsStats} connected={toolsConnected} />
      )}
      {activeTab === 'farbpalette' && (
        <FarbpaletteTab stats={toolsStats} connected={toolsConnected} />
      )}
    </div>
  );
}

/* ── Not-Connected Fallback ── */
function ToolsNotConnected({ name }: { name: string }) {
  return (
    <div className={`${cardCls} text-center py-12`}>
      <AlertTriangle className="mx-auto mb-3 text-amber-500" size={32} />
      <h2 className="text-lg font-semibold text-slate-800 mb-2">{name} nicht verbunden</h2>
      <p className="text-slate-500 text-sm">
        Der Tools-Container (<code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">rla-tools-app</code>) ist nicht erreichbar.
      </p>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   UPLOAD TAB
   ══════════════════════════════════════════════════════════════════ */
function UploadTab({ stats, shares, connected }: {
  stats: UploadStats | null;
  shares: Share[];
  connected: boolean;
}) {
  if (!connected || !stats) {
    return (
      <div className={`${cardCls} text-center py-12`}>
        <AlertTriangle className="mx-auto mb-3 text-amber-500" size={32} />
        <h2 className="text-lg font-semibold text-slate-800 mb-2">Upload-DB nicht verbunden</h2>
        <p className="text-slate-500 text-sm">
          <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">UPLOAD_DATABASE_URL</code> nicht konfiguriert oder DB nicht erreichbar.
        </p>
      </div>
    );
  }

  const activeShares = shares.filter(s => !s.deleted_at && !isExpired(s.expires_at));
  const expiredShares = shares.filter(s => s.deleted_at || isExpired(s.expires_at));

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <a href="https://upload.rla-solutions.de" target="_blank" rel="noopener noreferrer" className={btnSecondary}>
          <ExternalLink size={14} /> upload.rla-solutions.de
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={statCls}>
          <div className={iconBgCls('bg-blue-100 text-blue-600')}><FileText size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{stats.totalFiles}</div>
            <div className="text-xs text-slate-500">Dateien gesamt</div>
          </div>
        </div>
        <div className={statCls}>
          <div className={iconBgCls('bg-purple-100 text-purple-600')}><HardDrive size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{formatBytes(stats.totalSizeBytes)}</div>
            <div className="text-xs text-slate-500">Speicherverbrauch</div>
          </div>
        </div>
        <div className={statCls}>
          <div className={iconBgCls('bg-green-100 text-green-600')}><Upload size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{stats.activeShares}</div>
            <div className="text-xs text-slate-500">Aktive Shares</div>
          </div>
        </div>
        <div className={statCls}>
          <div className={iconBgCls('bg-amber-100 text-amber-600')}><Download size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{stats.totalDownloads}</div>
            <div className="text-xs text-slate-500">Downloads gesamt</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={cardCls}>
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-blue-600" /> Aktivität
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Heute</span>
              <span className="text-lg font-bold text-slate-900">{stats.sharesToday}</span>
            </div>
            <div className="border-t border-slate-100" />
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Diese Woche</span>
              <span className="text-lg font-bold text-slate-900">{stats.sharesThisWeek}</span>
            </div>
            <div className="border-t border-slate-100" />
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Dieser Monat</span>
              <span className="text-lg font-bold text-slate-900">{stats.sharesThisMonth}</span>
            </div>
          </div>
        </div>
        <div className={cardCls}>
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-4">
            <Shield size={16} className="text-green-600" /> Sicherheit
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Passwortgeschützt</span>
              <span className="text-lg font-bold text-slate-900">{stats.passwordProtected}</span>
            </div>
            <div className="border-t border-slate-100" />
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Ohne Passwort</span>
              <span className="text-lg font-bold text-slate-900">{stats.activeShares - stats.passwordProtected}</span>
            </div>
            <div className="border-t border-slate-100" />
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Abgelaufen / Gelöscht</span>
              <span className="text-lg font-bold text-slate-900">{stats.totalShares - stats.activeShares}</span>
            </div>
          </div>
        </div>
        <div className={cardCls}>
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-4">
            <Clock size={16} className="text-purple-600" /> Durchschnitt
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Dateien / Share</span>
              <span className="text-lg font-bold text-slate-900">
                {stats.activeShares > 0 ? (stats.totalFiles / stats.activeShares).toFixed(1) : '0'}
              </span>
            </div>
            <div className="border-t border-slate-100" />
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Downloads / Share</span>
              <span className="text-lg font-bold text-slate-900">
                {stats.totalShares > 0 ? (stats.totalDownloads / stats.totalShares).toFixed(1) : '0'}
              </span>
            </div>
            <div className="border-t border-slate-100" />
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Durchschn. Größe</span>
              <span className="text-lg font-bold text-slate-900">
                {stats.totalFiles > 0 ? formatBytes(Math.round(stats.totalSizeBytes / stats.totalFiles)) : '0'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Shares-Tabelle */}
      <div className={cardCls}>
        <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <Calendar size={16} className="text-blue-600" />
          Aktive Shares ({activeShares.length})
        </h3>
        {activeShares.length === 0 ? (
          <p className="text-slate-400 text-sm py-4 text-center">Keine aktiven Shares vorhanden.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase tracking-wider">
                  <th className="pb-3 pr-4">Titel / Slug</th>
                  <th className="pb-3 pr-4">Dateien</th>
                  <th className="pb-3 pr-4">Größe</th>
                  <th className="pb-3 pr-4">Downloads</th>
                  <th className="pb-3 pr-4">Erstellt</th>
                  <th className="pb-3 pr-4">Gültig bis</th>
                  <th className="pb-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {activeShares.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50 transition">
                    <td className="py-3 pr-4">
                      <div className="font-medium text-slate-800">
                        {s.title || <span className="text-slate-400 italic">Ohne Titel</span>}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        <a href={`https://upload.rla-solutions.de/s/${s.slug}`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition">
                          /s/{s.slug}
                        </a>
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-slate-700">{s.file_count}</td>
                    <td className="py-3 pr-4 text-slate-700">{formatBytes(s.total_size)}</td>
                    <td className="py-3 pr-4 text-slate-700">
                      {s.current_downloads}
                      {s.max_downloads && <span className="text-slate-400">/{s.max_downloads}</span>}
                    </td>
                    <td className="py-3 pr-4 text-slate-500 text-xs">{formatDate(s.created_at)}</td>
                    <td className="py-3 pr-4 text-slate-500 text-xs">{formatDate(s.expires_at)}</td>
                    <td className="py-3">
                      <div className="flex gap-1 flex-wrap">
                        {s.password_protected && (
                          <span className={badgeCls('bg-green-100 text-green-700')}><Lock size={10} /> Passwort</span>
                        )}
                        {s.max_downloads && (
                          <span className={badgeCls('bg-amber-100 text-amber-700')}>Limit {s.max_downloads}</span>
                        )}
                        {!s.password_protected && !s.max_downloads && (
                          <span className={badgeCls('bg-blue-100 text-blue-700')}>Öffentlich</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {expiredShares.length > 0 && (
        <details className={cardCls}>
          <summary className="text-sm font-semibold text-slate-500 cursor-pointer hover:text-slate-700 transition flex items-center gap-2">
            <AlertTriangle size={16} className="text-slate-400" />
            Abgelaufen / Gelöscht ({expiredShares.length})
          </summary>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase tracking-wider">
                  <th className="pb-3 pr-4">Titel / Slug</th>
                  <th className="pb-3 pr-4">Dateien</th>
                  <th className="pb-3 pr-4">Größe</th>
                  <th className="pb-3 pr-4">Downloads</th>
                  <th className="pb-3 pr-4">Erstellt</th>
                  <th className="pb-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {expiredShares.map((s) => (
                  <tr key={s.id} className="text-slate-400">
                    <td className="py-3 pr-4">
                      <div>{s.title || <span className="italic">Ohne Titel</span>}</div>
                      <div className="text-xs mt-0.5">/s/{s.slug}</div>
                    </td>
                    <td className="py-3 pr-4">{s.file_count}</td>
                    <td className="py-3 pr-4">{formatBytes(s.total_size)}</td>
                    <td className="py-3 pr-4">{s.current_downloads}</td>
                    <td className="py-3 pr-4 text-xs">{formatDate(s.created_at)}</td>
                    <td className="py-3">
                      <span className={badgeCls(s.deleted_at ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500')}>
                        {s.deleted_at ? 'Gelöscht' : 'Abgelaufen'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   CONVERTER TAB
   ══════════════════════════════════════════════════════════════════ */
function ConverterTab({ stats, connected }: {
  stats: ConverterStats | null;
  connected: boolean;
}) {
  if (!connected || !stats) {
    return (
      <div className={`${cardCls} text-center py-12`}>
        <AlertTriangle className="mx-auto mb-3 text-amber-500" size={32} />
        <h2 className="text-lg font-semibold text-slate-800 mb-2">Converter nicht verbunden</h2>
        <p className="text-slate-500 text-sm">
          Der Converter-Container (<code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">rla-converter-app</code>) ist nicht erreichbar.
        </p>
      </div>
    );
  }

  const topTypes = Object.entries(stats.byType)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10);

  const maxCount = topTypes.length > 0 ? (topTypes[0]?.[1] ?? 1) : 1;

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <a href="https://converter.rla-solutions.de" target="_blank" rel="noopener noreferrer" className={btnSecondary}>
          <ExternalLink size={14} /> converter.rla-solutions.de
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={statCls}>
          <div className={iconBgCls('bg-purple-100 text-purple-600')}><Repeat size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{stats.totalConversions}</div>
            <div className="text-xs text-slate-500">Konvertierungen gesamt</div>
          </div>
        </div>
        <div className={statCls}>
          <div className={iconBgCls('bg-blue-100 text-blue-600')}><Zap size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{stats.conversionsToday}</div>
            <div className="text-xs text-slate-500">Heute</div>
          </div>
        </div>
        <div className={statCls}>
          <div className={iconBgCls('bg-green-100 text-green-600')}><HardDrive size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{formatBytes(stats.totalInputBytes)}</div>
            <div className="text-xs text-slate-500">Verarbeitet (Input)</div>
          </div>
        </div>
        <div className={statCls}>
          <div className={iconBgCls('bg-amber-100 text-amber-600')}><Download size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{formatBytes(stats.totalOutputBytes)}</div>
            <div className="text-xs text-slate-500">Ausgabe (Output)</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={`${cardCls} lg:col-span-2`}>
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-4">
            <BarChart3 size={16} className="text-purple-600" /> Beliebte Konvertierungen
          </h3>
          {topTypes.length === 0 ? (
            <p className="text-slate-400 text-sm py-4 text-center">Noch keine Konvertierungen durchgeführt.</p>
          ) : (
            <div className="space-y-3">
              {topTypes.map(([type, count]) => (
                <div key={type}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-slate-700">{type.toUpperCase()}</span>
                    <span className="text-sm font-bold text-slate-900">{count}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-purple-500 rounded-full transition-all"
                      style={{ width: `${(count / maxCount) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={cardCls}>
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-4">
            <Clock size={16} className="text-blue-600" /> System
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Uptime</span>
              <span className="text-sm font-bold text-slate-900">{formatUptime(stats.uptime)}</span>
            </div>
            <div className="border-t border-slate-100" />
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Temp-Dateien</span>
              <span className="text-sm font-bold text-slate-900">{stats.tmpFileCount}</span>
            </div>
            <div className="border-t border-slate-100" />
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Temp-Größe</span>
              <span className="text-sm font-bold text-slate-900">{formatBytes(stats.tmpSizeBytes)}</span>
            </div>
            <div className="border-t border-slate-100" />
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Fehler</span>
              <span className={`text-sm font-bold ${stats.errors > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {stats.errors}
              </span>
            </div>
            <div className="border-t border-slate-100" />
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Gestartet</span>
              <span className="text-xs text-slate-500">{formatDate(stats.startedAt)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   QR-CODE TAB
   ══════════════════════════════════════════════════════════════════ */
function QrCodeTab({ stats, connected }: { stats: ToolsStats | null; connected: boolean }) {
  if (!connected || !stats) return <ToolsNotConnected name="QR-Code Generator" />;

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <a href="https://tools.rla-solutions.de/qrcode" target="_blank" rel="noopener noreferrer" className={btnSecondary}>
          <ExternalLink size={14} /> tools.rla-solutions.de/qrcode
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className={statCls}>
          <div className={iconBgCls('bg-emerald-100 text-emerald-600')}><QrCode size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{stats.qrcode}</div>
            <div className="text-xs text-slate-500">QR-Codes generiert</div>
          </div>
        </div>
        <div className={statCls}>
          <div className={iconBgCls('bg-blue-100 text-blue-600')}><Clock size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{formatUptime(stats.uptime)}</div>
            <div className="text-xs text-slate-500">Uptime</div>
          </div>
        </div>
        <div className={statCls}>
          <div className={iconBgCls('bg-slate-100 text-slate-600')}><Zap size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{formatDate(stats.startedAt)}</div>
            <div className="text-xs text-slate-500">Gestartet am</div>
          </div>
        </div>
      </div>

      <div className={cardCls}>
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
          <QrCode size={16} className="text-emerald-600" /> Funktionen
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {['URL', 'Text', 'WLAN', 'vCard'].map(type => (
            <div key={type} className="bg-slate-50 rounded-lg p-3 text-center">
              <div className="text-sm font-medium text-slate-700">{type}</div>
              <div className="text-xs text-slate-400 mt-1">QR-Typ</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   PASSWORT TAB
   ══════════════════════════════════════════════════════════════════ */
function PasswortTab({ stats, connected }: { stats: ToolsStats | null; connected: boolean }) {
  if (!connected || !stats) return <ToolsNotConnected name="Passwort Generator" />;

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <a href="https://tools.rla-solutions.de/passwortgen" target="_blank" rel="noopener noreferrer" className={btnSecondary}>
          <ExternalLink size={14} /> tools.rla-solutions.de/passwortgen
        </a>
      </div>

      <div className={cardCls}>
        <div className="flex items-start gap-4">
          <div className={iconBgCls('bg-violet-100 text-violet-600')}>
            <KeyRound size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Client-seitig</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Der Passwort-Generator läuft vollständig im Browser des Nutzers mit der Web Crypto API.
              Es werden keine Passwörter an den Server gesendet — daher gibt es keine serverseitigen Statistiken.
            </p>
          </div>
        </div>
      </div>

      <div className={cardCls}>
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
          <Shield size={16} className="text-violet-600" /> Sicherheitsmerkmale
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { title: 'Web Crypto API', desc: 'Kryptografisch sichere Zufallszahlen' },
            { title: 'Kein Server-Kontakt', desc: 'Passwörter verlassen nie den Browser' },
            { title: 'Anpassbar', desc: 'Länge, Groß-/Kleinbuchstaben, Zahlen, Sonderzeichen' },
            { title: 'Stärke-Anzeige', desc: 'Visuelle Bewertung der Passwortstärke' },
          ].map(item => (
            <div key={item.title} className="bg-slate-50 rounded-lg p-4">
              <div className="text-sm font-medium text-slate-700">{item.title}</div>
              <div className="text-xs text-slate-500 mt-1">{item.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   KURZLINKS TAB
   ══════════════════════════════════════════════════════════════════ */
function KurzlinksTab({ stats, connected }: { stats: ToolsStats | null; connected: boolean }) {
  if (!connected || !stats) return <ToolsNotConnected name="Kurzlink-Dienst" />;

  const sl = stats.shortlinks;

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <a href="https://tools.rla-solutions.de/short" target="_blank" rel="noopener noreferrer" className={btnSecondary}>
          <ExternalLink size={14} /> tools.rla-solutions.de/short
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={statCls}>
          <div className={iconBgCls('bg-sky-100 text-sky-600')}><Link2 size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{sl.total}</div>
            <div className="text-xs text-slate-500">Links gesamt</div>
          </div>
        </div>
        <div className={statCls}>
          <div className={iconBgCls('bg-blue-100 text-blue-600')}><MousePointerClick size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{sl.totalClicks}</div>
            <div className="text-xs text-slate-500">Klicks gesamt</div>
          </div>
        </div>
        <div className={statCls}>
          <div className={iconBgCls('bg-green-100 text-green-600')}><TrendingUp size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{sl.today}</div>
            <div className="text-xs text-slate-500">Heute erstellt</div>
          </div>
        </div>
        <div className={statCls}>
          <div className={iconBgCls('bg-purple-100 text-purple-600')}><Hash size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">
              {sl.total > 0 ? (sl.totalClicks / sl.total).toFixed(1) : '0'}
            </div>
            <div className="text-xs text-slate-500">Klicks / Link</div>
          </div>
        </div>
      </div>

      {/* Top Links Tabelle */}
      <div className={cardCls}>
        <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <BarChart3 size={16} className="text-sky-600" />
          Top Links
        </h3>
        {sl.topLinks.length === 0 ? (
          <p className="text-slate-400 text-sm py-4 text-center">Noch keine Kurzlinks erstellt.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase tracking-wider">
                  <th className="pb-3 pr-4">Kurzlink</th>
                  <th className="pb-3 pr-4">Ziel</th>
                  <th className="pb-3 pr-4">Klicks</th>
                  <th className="pb-3">Erstellt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sl.topLinks.map((link) => (
                  <tr key={link.code} className="hover:bg-slate-50 transition">
                    <td className="py-3 pr-4">
                      <a
                        href={`https://link.rla-solutions.de/${link.code}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-600 hover:text-sky-800 font-medium transition"
                      >
                        link.rla-solutions.de/{link.code}
                      </a>
                    </td>
                    <td className="py-3 pr-4 text-slate-600 max-w-xs truncate">
                      <a href={link.target_url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition">
                        {link.target_url.length > 60 ? link.target_url.slice(0, 60) + '...' : link.target_url}
                      </a>
                    </td>
                    <td className="py-3 pr-4">
                      <span className="font-bold text-slate-900">{link.clicks}</span>
                    </td>
                    <td className="py-3 text-slate-500 text-xs">{formatDate(link.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   BILDBEARBEITUNG TAB
   ══════════════════════════════════════════════════════════════════ */
function BildbearbeitungTab({ stats, connected }: { stats: ToolsStats | null; connected: boolean }) {
  if (!connected || !stats) return <ToolsNotConnected name="Bildbearbeitung" />;

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <a href="https://tools.rla-solutions.de/bildbearbeitung" target="_blank" rel="noopener noreferrer" className={btnSecondary}>
          <ExternalLink size={14} /> tools.rla-solutions.de/bildbearbeitung
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className={statCls}>
          <div className={iconBgCls('bg-orange-100 text-orange-600')}><ImageIcon size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{stats.image}</div>
            <div className="text-xs text-slate-500">Bilder bearbeitet</div>
          </div>
        </div>
        <div className={statCls}>
          <div className={iconBgCls('bg-blue-100 text-blue-600')}><Clock size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{formatUptime(stats.uptime)}</div>
            <div className="text-xs text-slate-500">Uptime</div>
          </div>
        </div>
        <div className={statCls}>
          <div className={iconBgCls('bg-slate-100 text-slate-600')}><Zap size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{formatDate(stats.startedAt)}</div>
            <div className="text-xs text-slate-500">Gestartet am</div>
          </div>
        </div>
      </div>

      <div className={cardCls}>
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
          <ImageIcon size={16} className="text-orange-600" /> Funktionen
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { title: 'Größe ändern', desc: 'Breite & Höhe anpassen' },
            { title: 'Qualität', desc: 'JPEG/WebP-Qualität steuern' },
            { title: 'Format konvertieren', desc: 'JPEG, PNG, WebP' },
          ].map(item => (
            <div key={item.title} className="bg-slate-50 rounded-lg p-4">
              <div className="text-sm font-medium text-slate-700">{item.title}</div>
              <div className="text-xs text-slate-500 mt-1">{item.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   FARBPALETTE TAB
   ══════════════════════════════════════════════════════════════════ */
function FarbpaletteTab({ stats, connected }: { stats: ToolsStats | null; connected: boolean }) {
  if (!connected || !stats) return <ToolsNotConnected name="Farbpalette" />;

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <a href="https://tools.rla-solutions.de/farbpalette" target="_blank" rel="noopener noreferrer" className={btnSecondary}>
          <ExternalLink size={14} /> tools.rla-solutions.de/farbpalette
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className={statCls}>
          <div className={iconBgCls('bg-pink-100 text-pink-600')}><Palette size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{stats.colors}</div>
            <div className="text-xs text-slate-500">Farben extrahiert</div>
          </div>
        </div>
        <div className={statCls}>
          <div className={iconBgCls('bg-blue-100 text-blue-600')}><Clock size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{formatUptime(stats.uptime)}</div>
            <div className="text-xs text-slate-500">Uptime</div>
          </div>
        </div>
        <div className={statCls}>
          <div className={iconBgCls('bg-slate-100 text-slate-600')}><Zap size={24} /></div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{formatDate(stats.startedAt)}</div>
            <div className="text-xs text-slate-500">Gestartet am</div>
          </div>
        </div>
      </div>

      <div className={cardCls}>
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
          <Palette size={16} className="text-pink-600" /> Funktionen
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed">
          Extrahiert die 8 dominantesten Farben aus hochgeladenen Bildern mittels Pixel-Sampling und Farb-Quantisierung.
          Zeigt HEX- und RGB-Werte mit Prozentanteil an.
        </p>
      </div>
    </div>
  );
}
